import React from 'react';
import { motion } from 'framer-motion';

const ImpulseBuyingLandingPage = ({ goToInput, theme }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Impulse Buying Calculator | Track Your Spontaneous Purchases
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Calculate Your Impulse Purchase Score</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Track your spontaneous buys, understand your impulse triggers, and make smarter shopping decisions.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your Impulse Buy
            </motion.button>
          </div>
          <div className="text-8xl">💸</div>
        </div>
      </div>
      
      {/* What causes impulse buying section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Why We Make Impulse Purchases</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🧠</span>
              <div>
                <h3 className="font-bold dark:text-white">Dopamine Rush</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  The brain's reward system triggers a pleasure response when we make spontaneous purchases.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">😔</span>
              <div>
                <h3 className="font-bold dark:text-white">Emotional Relief</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Shopping to escape negative feelings like boredom, stress, or sadness.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">👀</span>
              <div>
                <h3 className="font-bold dark:text-white">FOMO Effect</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Fear of missing out on limited-time deals or exclusive products.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🛒</span>
              <div>
                <h3 className="font-bold dark:text-white">Store Psychology</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Strategic store layouts and online design tactics engineered to trigger impulse purchases.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* How Guilt Gauge helps */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Our Impulse Buying Calculator Helps</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Identify Your Impulse Triggers
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Discover which emotions and situations lead to your unplanned purchases.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Calculate Your Impulse Score
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Get a personalized assessment of each unplanned purchase and its impact.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Receive Personalized Strategies
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Get tailored tips to reduce spontaneous spending and implement healthier habits.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Track Your Progress
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Visualize your improvement over time as you develop mindful spending habits.
            </p>
          </div>
        </div>
      </section>
      
      {/* Impulse Buyer Types */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Which Impulse Buyer Type Are You?</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 text-center">
            <div className="text-3xl mb-2">🎯</div>
            <h3 className="font-bold dark:text-white">FOMO Shopper</h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm mt-2">
              You panic-buy limited editions and can't resist "last chance" deals.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 text-center">
            <div className="text-3xl mb-2">🎭</div>
            <h3 className="font-bold dark:text-white">Emotional Buyer</h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm mt-2">
              Your purchases are directly linked to your current emotional state.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 text-center">
            <div className="text-3xl mb-2">🔍</div>
            <h3 className="font-bold dark:text-white">Browser Turned Buyer</h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm mt-2">
              You intended "just to look" but somehow always end up purchasing.
            </p>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5">
        <h2 className="text-xl font-bold mb-4 dark:text-white">Impulse Buying By The Numbers</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">84%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of shoppers make impulse purchases</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">$5,400</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">average annual impulse spending per person</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">40%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of all e-commerce purchases are impulse buys</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">3 of 4</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">impulse buyers later regret their purchase</p>
          </div>
        </div>
      </section>
      
      {/* Testimonials section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Others Controlled Their Impulse Spending</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I was spending hundreds every month on random Amazon purchases. After tracking my impulse buys for two months, I identified my triggers and reduced my spontaneous spending by 70%."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Alex T.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Former Prime Day Addict</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "The guilt calculator showed me I was impulse shopping whenever I felt stressed at work. Now I have alternative stress-relief strategies and my credit card bill is much healthier!"
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Morgan L.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Mindful Spender</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section - important for SEO */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About Impulse Buying</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How do I stop impulse buying?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Use our impulse calculator to track your spontaneous purchases, implement a 24-hour waiting rule for non-essential items, unsubscribe from retail emails, and make shopping lists before you shop. Identifying your personal triggers is the first step to change.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Is impulse buying a sign of mental illness?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Occasional impulse purchases are normal, but compulsive buying disorder is a recognized condition characterized by uncontrollable shopping urges despite negative consequences. If your impulse spending is causing significant financial or emotional distress, consider speaking with a mental health professional.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How do stores encourage impulse buying?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Retailers use psychological triggers like strategic product placement, limited-time offers, point-of-sale displays, and carefully designed store layouts. Online stores use countdown timers, pop-ups, and personalized recommendations to create urgency and encourage spontaneous purchases.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">What's the 30-day rule for impulse buying?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              The 30-day rule involves waiting 30 days before purchasing a non-essential item you're tempted to buy impulsively. During this cooling-off period, you often find the impulse fades, or you realize you don't truly need or want the item after all.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready to Tame Your Impulse Spending?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Start tracking your spontaneous purchases and develop mindful shopping habits.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Calculate Your Impulse Score Now
        </motion.button>
      </section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Impulse Buying</h2>
          <p className="mb-3">
            Impulse buying is the unplanned purchase of products and services, triggered by emotions, external stimuli, or cognitive reactions. These spontaneous purchases often lead to buyer's remorse, financial strain, and the accumulation of unwanted items.
          </p>
          <p className="mb-3">
            Our Impulse Buying Calculator helps you understand and address these behaviors by tracking your emotional state during purchases, analyzing your spending patterns, and providing personalized strategies to develop more mindful shopping habits.
          </p>
          <p>
            Whether you're dealing with online shopping addiction, sale-driven impulse buys, or emotional spending, our free tool helps you gain awareness and control over your spontaneous purchasing decisions.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default ImpulseBuyingLandingPage;
